import type { Price } from "@@/shared/prices/price";
import type { Translatable } from "@@/shared/translations/translation";
import type { MissingOptionValue } from "../acquire/resources/resource-helper";
import type { ResourceId } from "../acquire/resources/resource-id";
import type { Time } from "../date-time/time";

type VerificationError = {
    _type: "VERIFICATION_ERROR";
    statusCode: number;
    reason: Translatable;
    results: VerificationTaskResult[];
};

const createVerificationError = (
    params: Omit<VerificationError, "_type">,
): VerificationError => {
    return {
        _type: "VERIFICATION_ERROR",
        ...params,
    };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isVerificationError = (error: any): error is VerificationError => {
    return error._type === "VERIFICATION_ERROR";
};

type VerificationTaskResult =
    | {
          type: "PRODUCT_NOT_FOUND";
          item: {
              productId: string;
              skuId: string;
          };
      }
    | {
          type: "PASSED";
          item: {
              id: string;
              title: Translatable;
          };
      }
    | {
          type: "OPTIONAL_NOT_FOUND";
          item: {
              id: string;
              title: Translatable;
          };
          missing: string[];
      }
    | {
          type: "BAD_REQUEST_NO_PRICE_AVAILABLE";
          item: {
              id: string;
              title: Translatable;
          };
          requestedPrice: Price;
          requestedPricePerItem: Price;
      }
    | {
          type: "BAD_REQUEST_PRICE_MISMATCH";
          item: {
              id: string;
              title: Translatable;
          };
          requestedPrice: Price;
          actualPrice: Price;
          actualPricePerItem: Price;
      }
    | {
          type: "BAD_REQUEST_PRICE_TO_SMALL";
          item: {
              id: string;
              title: Translatable;
          };
          requestedPrice: Price;
          minPrice: Price;
          minPricePerItem: Price;
      }
    | {
          type: "BAD_REQUEST_PRICE_TO_LARGE";
          item: {
              id: string;
              title: Translatable;
          };
          requestedPrice: Price;
          maxPrice: Price;
          maxPricePerItem: Price;
      }
    | {
          type: "OUT_OF_DATE_DELIVERY";

          shippingId: string;
          slot: Time;
      }
    | {
          type: "SHIPPING_NOT_FOUND";
          shippingId: string;
      }
    | {
          type: "SHIPPING_SLOT_IS_FULL";
          shippingId: string;
          slot: Time | undefined;
      }
    | {
          type: "SHIPPING_SLOT_NOT_FOUND";
          shippingId: string;
          slot: Time | undefined;
      }
    | {
          type: "SESSION_NOT_FOUND";
          sessionId: string;
      }
    | {
          type: "SESSION_FULL";
          sessionId: string;
      }
    | {
          type: "RESOURCE_IS_ALREADY_BOOKED";
          resourceId: ResourceId;
      }
    | {
          type: "UNABLE_TO_BOOK_ALL_OPTION_VALUES";
          missing: MissingOptionValue[];
      }
    | {
          type: "MISSING_RESERVATION_FOR_BOOKABLE";
      }
    | {
          type: "GIFT_CARD_AMOUNT_MISMATCH";
          code: string;
      }
    | {
          type: "GIFT_CARD_OUTDATED";
          code: string;
      }
    | {
          type: "DISCOUNT_CODE_NOT_FOUND";
          code: string;
      }
    | {
          type: "DISCOUNT_CODE_NOT_VALID";
          code: string;
      }
    | {
          type: "DISCOUNT_CODE_VALUE_MISMATCH";
          code: string;
      };

export { createVerificationError, isVerificationError };
export type { VerificationError, VerificationTaskResult };
